import { BaseGrid } from './BaseGrid';
import { Cell } from './Cell';
import { GridType } from './GridType';
import { HexMath, HexOrientation, HexCoord } from './HexMath';

export interface HexGridConfig {
  rows: number;
  cols: number;
  orientation: HexOrientation;
}

export class HexGrid extends BaseGrid {
  public readonly orientation: HexOrientation;

  constructor(config: HexGridConfig) {
    super({
      rows: config.rows,
      cols: config.cols,
      gridType: config.orientation === HexOrientation.POINTY ? GridType.HEX_POINTY : GridType.HEX_FLAT
    });
    this.orientation = config.orientation;
  }

  getNeighbors(cell: Cell): Cell[] {
    const neighbors: Cell[] = [];
    const axial = HexMath.offsetToAxial(cell.col, cell.row, this.orientation);
    const neighborCoords = HexMath.getHexNeighbors(axial.q, axial.r);

    for (const coord of neighborCoords) {
      const offset = HexMath.axialToOffset(coord.q, coord.r, this.orientation);
      const neighbor = this.getCell(offset.row, offset.col);

      if (neighbor && neighbor.isWalkable) {
        neighbors.push(neighbor);
      }
    }

    return neighbors;
  }

  calculateDistance(cellA: Cell, cellB: Cell): number {
    const axialA = HexMath.offsetToAxial(cellA.col, cellA.row, this.orientation);
    const axialB = HexMath.offsetToAxial(cellB.col, cellB.row, this.orientation);
    return HexMath.hexDistance(axialA.q, axialA.r, axialB.q, axialB.r);
  }

  offsetToAxial(col: number, row: number): HexCoord {
    return HexMath.offsetToAxial(col, row, this.orientation);
  }

  axialToOffset(q: number, r: number): { col: number; row: number } {
    return HexMath.axialToOffset(q, r, this.orientation);
  }

  clone(): HexGrid {
    const newGrid = new HexGrid({
      rows: this.rows,
      cols: this.cols,
      orientation: this.orientation
    });

    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        const originalCell = this.cells[row][col];
        const newCell = newGrid.getCellAt(row, col);
        newCell.type = originalCell.type;
        newCell.weight = originalCell.weight;
      }
    }

    return newGrid;
  }
}
